// The one print path for TabOn.
//
// Why this file exists: on a tablet — the device actually on the pass and the counter — Chrome
// blocks window.open() once it is called after an `await`, and it blocks it *silently*. Every print
// we do runs after an await (we POST to stamp printed_at, then print), so a pop-up KOT could simply
// never appear and no one — not the waiter, not the kitchen — would know the docket was lost. A
// blocked KOT stops a kitchen.
//
// So nothing here opens a window. We render into a hidden, same-origin <iframe> and print THAT
// frame: there is no pop-up for the browser to block. Two shapes are in use today and both live
// here:
//   • printHtml(html)   — a document we build ourselves as an HTML string (the kitchen docket / KOT)
//   • printInvoice(id)  — an app route (/invoice/:id) loaded in the frame and printed once the page
//                         itself signals that the bill is on screen
// Every entry point reports its own failure to the user (a toast) and resolves to false, so a print
// can never fail silently — which is the whole reason this file exists.
//
// The transport changes once more when the local ESC/POS printer agent lands; keep it in here so
// that change touches one file.

import { toast } from "sonner";
import { api } from "./api";

// A hidden, off-screen frame. Same-origin, so we can reach its document/window and print it.
function makeFrame() {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.style.cssText = "position:fixed;left:-10000px;top:0;width:1px;height:1px;border:0;opacity:0;";
  document.body.appendChild(frame);
  return frame;
}

// window.print() returns as soon as the dialog is dismissed, but some engines snapshot the frame a
// tick later — so detach after a short delay rather than immediately, or the last job can be lost.
function dropFrame(frame) {
  if (!frame) return;
  setTimeout(() => { try { frame.remove(); } catch (_) {} }, 1000);
}

// --- Print timing → kiosk-mode inference (AH-1) --------------------------------------------------
// window.print() BLOCKS until the print dialog is dismissed. With Chrome's --kiosk-printing there is
// no dialog and it returns almost immediately, so the elapsed time across the call is the ONLY signal
// we have for whether kiosk printing is actually on — no browser API reports printer state, and
// `afterprint` fires the same whether paper came out or the user cancelled. We never claim paper came
// out; we only know a print was attempted, the browser accepted it, and it did or did not go through a
// dialog. The thresholds are deliberately wide, and the venue-level verdict comes from the
// distribution over many prints (the server log, AH-3), never one sample — so `unknown` is recorded
// but never warned on.
function classifyPrint(durationMs) {
  if (durationMs < 400) return "silent";      // no dialog — kiosk printing almost certainly active
  if (durationMs > 1500) return "dialog";     // a human almost certainly dismissed a dialog
  return "unknown";                            // slow machine or borderline — record, do not warn
}

// Once-per-session guard for the dialog warning (AH-2). A reload within the same kiosk tab must not
// re-warn, so we also remember it in sessionStorage; if that is unavailable the module flag still
// holds for this page load. Storage access is wrapped — it must never break a print.
let _dialogWarned = false;
const DIALOG_WARNED_KEY = "tabon:print-dialog-warned";
function _alreadyWarnedDialog() {
  if (_dialogWarned) return true;
  try { if (sessionStorage.getItem(DIALOG_WARNED_KEY)) { _dialogWarned = true; } } catch (_) {}
  return _dialogWarned;
}
function _markDialogWarned() {
  _dialogWarned = true;
  try { sessionStorage.setItem(DIALOG_WARNED_KEY, "1"); } catch (_) {}
}

// The single sink for a finished print's measurement — { ok, mode, durationMs } — called once per
// attempt from BOTH print paths, with `meta` { document, refType, refId } from the caller. Every print
// — KOT or bill — is measured, warned and logged here identically. It must never throw into the print
// path.
function recordPrint(result, meta) {
  try {
    console.debug("[tabon-print]", result.mode, Math.round(result.durationMs) + "ms", result.ok ? "ok" : "failed");
  } catch (_) { /* observability must never break a print */ }
  // Fire-and-forget server log (AH-3). It is pure observability: do NOT await it (a slow or down log
  // must never delay a docket), and swallow every error (a logging failure must never fail a print).
  try {
    api.post("/print-log", {
      document: (meta && meta.document) || "bill",
      ref_type: (meta && meta.refType) || null,
      ref_id: (meta && meta.refId) || null,
      outcome: result.ok ? "completed" : "failed",
      mode: result.mode,
      duration_ms: Math.round(result.durationMs),
    }).catch(() => {});
  } catch (_) { /* never let logging break a print */ }
  // A `dialog` classification means Chrome was opened without the --kiosk-printing shortcut (AH-2):
  // the staff member launched it from the taskbar, Start menu or a WhatsApp link, dialogs are back,
  // and nothing else on screen says so. Warn ONCE per session — never on `unknown` (a slow machine is
  // not a misconfiguration), never in the error style (the print SUCCEEDED, after two extra clicks),
  // and never blocking. It just has to catch the wrong-shortcut mistake before someone picks "Save as
  // PDF" by accident.
  if (result.mode === "dialog" && !_alreadyWarnedDialog()) {
    _markDialogWarned();
    try {
      toast.warning(
        "Print dialog appeared — Chrome was opened without the TabOn shortcut. Close Chrome and reopen it from the TabOn icon on the taskbar.",
        { duration: 10000 },
      );
    } catch (_) { /* a toast failure must never break a print */ }
  }
}

// Print a document we build ourselves (the KOT). Write the full HTML into the frame, wait a frame
// for layout/fonts so the first docket isn't blank, then print the FRAME — not the host page.
export function printHtml(html, label = "docket", meta) {
  return new Promise((resolve) => {
    let frame;
    const fail = (why) => {
      try { if (frame) frame.remove(); } catch (_) {}
      toast.error(`Couldn't print the ${label}. ${why || "Please try again."}`);
      resolve(false);
    };
    try {
      frame = makeFrame();
      const win = frame.contentWindow;
      if (!win) return fail("The print view could not be opened.");
      win.document.open();
      win.document.write(html);
      win.document.close();
      const print = () => {
        try {
          win.focus();        // some engines print a blank page if the frame isn't focused first
          const t0 = performance.now();
          win.print();        // prints the frame's own document, not this page; BLOCKS on a dialog
          const durationMs = performance.now() - t0;
          dropFrame(frame);
          recordPrint({ ok: true, mode: classifyPrint(durationMs), durationMs }, meta);
          resolve(true);
        } catch (e) { fail(e && e.message); }
      };
      // Written content lays out synchronously; two rAFs let styles/fonts apply before we print.
      if (win.requestAnimationFrame) win.requestAnimationFrame(() => win.requestAnimationFrame(print));
      else setTimeout(print, 60);
    } catch (e) { fail(e && e.message); }
  });
}

// Print an app route (the settled invoice at /invoice/:id). The route is a client-rendered page: its
// `load` event fires on the shell BEFORE React has fetched and drawn the bill, so we must not print
// on load — we would print "Loading…". Instead the page, when it sees ?print=1, posts a `tabon-print`
// message once the bill (or its not-found state) is actually on screen, and prints itself. We wait
// for that signal and treat silence past a timeout as a failure, so a bill that never renders is
// still reported rather than hanging unseen in a hidden frame.
function printRoute(url, label = "bill", meta) {
  return new Promise((resolve) => {
    let frame, renderTimer, safetyTimer, settled = false, stopped = false;
    const origin = window.location.origin;
    const stopListening = () => {
      if (stopped) return;
      stopped = true;
      window.removeEventListener("message", onMessage);
      clearTimeout(renderTimer);
      clearTimeout(safetyTimer);
    };
    const finish = (ok, why) => {
      if (settled) return;
      settled = true;
      clearTimeout(renderTimer);
      if (!ok) {
        stopListening();
        try { if (frame) frame.remove(); } catch (_) {}
        toast.error(`Couldn't print the ${label}. ${why || "Please try again."}`);
      }
      // On success we resolve now (the bill is on screen) but KEEP the frame and listener alive: the
      // page measures its own blocking window.print() and posts a `tabon-print-timing` follow-up AFTER
      // the dialog, which we need for the kiosk-mode classification (AH-1). It is cleaned up when that
      // arrives, or by the safety timer if it never does (e.g. an older invoice page).
      resolve(ok);
    };
    const onMessage = (e) => {
      if (e.origin !== origin || !frame || e.source !== frame.contentWindow) return;
      if (!e.data) return;
      if (e.data.type === "tabon-print") {
        finish(!!e.data.ok, "The bill could not be loaded.");
      } else if (e.data.type === "tabon-print-timing") {
        recordPrint({ ok: e.data.ok !== false, mode: classifyPrint(e.data.durationMs), durationMs: e.data.durationMs }, meta);
        stopListening();
        dropFrame(frame);
      }
    };
    try {
      frame = makeFrame();
      window.addEventListener("message", onMessage);
      frame.addEventListener("error", () => finish(false, "The bill could not be loaded."));
      // Generous: the page must boot, fetch the invoice and render before it can signal.
      renderTimer = setTimeout(() => finish(false, "The bill took too long to open."), 15000);
      // Backstop: if the timing follow-up never comes (older page, or print threw), drop the frame
      // anyway so a hidden iframe never lingers. Longer than a human spends at a print dialog.
      safetyTimer = setTimeout(() => { stopListening(); dropFrame(frame); }, 30000);
      const sep = url.includes("?") ? "&" : "?";
      frame.src = `${url}${sep}print=1`;
    } catch (e) { finish(false, e && e.message); }
  });
}

// The settled invoice/receipt — the same document "Settle & print" produces. A re-issued bill passes
// { duplicate: true } so the page stamps DUPLICATE (AF-3); printRoute adds its own print=1.
export function printInvoice(orderId, { label = "bill", duplicate = false, meta } = {}) {
  return printRoute(`/invoice/${orderId}${duplicate ? "?duplicate=1" : ""}`, label, meta);
}

// The print page itself, for the dockets we build as HTML (the KOTs). A thermal roll is a fixed-width,
// marginless page; left to itself a browser assumes A4 with ~12mm margins and the docket prints tiny
// in a corner. Pin the page to the roll width with zero margins and let the height flow
// (size: <w>mm auto). 80mm is the common roll; 58mm is the small Bluetooth size, offered as a venue
// setting (venue.receipt_width_mm). Callers drop this into the document's <style>.
//
// NOT validated on hardware — there is no thermal printer in the loop yet, so this is built to the
// standard CSS @page model. The exact result still depends on the OS/driver and on the ESC/POS agent
// still to come; verify against the venue's actual printer before relying on the width.
export function pagePrintCss(widthMm = 80) {
  const w = widthMm === 58 ? 58 : 80;   // only the two standard roll widths
  return `
        @page { size: ${w}mm auto; margin: 0; }
        @media print {
          html, body { width: ${w}mm; margin: 0 !important; }
        }`;
}
